// CustomerDashboard.jsx  — warm cream/forest-green palette · customer charts
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ShoppingBag, Star, User, Bell, LogOut, ChevronRight,
  Package, CheckCircle, Clock, XCircle, TrendingUp,
  Heart, Edit3, Camera, Shield, Gift, RotateCcw,
  ChevronDown, Loader, Sparkles, ArrowUpRight, MapPin, Trash2,
} from "lucide-react";

/* ─── palette (refined luxe · cream × forest × gold foil) ─── */
const C = {
  bg:        "#f2ece1",   // deeper warm cream (more saturated)
  bgAlt:     "#ebe3d4",   // secondary cream for depth
  card:      "rgba(255,253,248,0.94)", // card bg
  cardAlt:   "rgba(252,248,240,0.88)",
  border:    "rgba(201,168,76,0.22)",  // gold border
  borderSoft:"rgba(31,61,58,0.08)",
  forest:    "#15312e",   // deeper forest (richer)
  forest2:   "#264f4a",   // medium forest
  forest3:   "#3a6b64",   // accent teal
  gold:      "#c9a84c",   // primary gold
  goldLight: "#e8d8a0",   // light gold
  goldDark:  "#a87c2a",   // dark gold
  goldFoil:  "#d4b85c",   // highlight foil
  text:      "#14140d",   // near-black text
  textMid:   "#5a4e35",   // mid brown
  textMuted: "#9a8a6a",   // muted
  success:   "#2d6a4f",
  successBg: "rgba(45,106,79,.1)",
  warn:      "#b8960c",
  warnBg:    "rgba(184,150,12,.1)",
  danger:    "#9b3030",
  dangerBg:  "rgba(155,48,48,.1)",
};

/* ─── Welcome banner background image ─── */
/* REPLACE this URL with your own image (local import also works: import banner from "./banner.jpg") */
const BANNER_IMAGE = "/2.png";

/* ─── API ─── */
const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const getToken = () => localStorage.getItem("dar_oulmes_token") || sessionStorage.getItem("dar_oulmes_token") || localStorage.getItem("access_token");
async function apiFetch(path, opts = {}) {
  const headers = { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json", ...opts.headers };
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || res.statusText); }
  if (res.status === 204) return null;
  return res.json();
}

/* ─── langs ─── */
const LANGS = [{ code:"en",label:"EN" },{ code:"fr",label:"FR" },{ code:"ar",label:"ع" }];

/* ─── status ─── */
const STATUS_CFG = {
  delivered:  { color:C.success, bg:C.successBg, icon:CheckCircle },
  processing: { color:C.warn,    bg:C.warnBg,    icon:Clock        },
  cancelled:  { color:C.danger,  bg:C.dangerBg,  icon:XCircle      },
  pending:    { color:C.warn,    bg:C.warnBg,    icon:Clock        },
  confirmed:  { color:C.success, bg:C.successBg, icon:CheckCircle  },
};
/* !FIX — the backend stores delivery state on `order.deliveryStatus` as an
   uppercase Prisma enum (UNASSIGNED | ASSIGNED | ACCEPTED | OUT_FOR_DELIVERY |
   DELIVERED | FAILED). The UI previously read `order.status` (which doesn't
   exist), so DELIVERED orders fell through to the "processing" default and
   showed as "In Progress". This helper is the single source of truth that
   maps the DB enum to the lowercase UI status key. */
function getOrderStatus(order) {
  if (!order?.isConfirmed) return "cancelled";
  const ds = String(order?.deliveryStatus || "").toUpperCase();
  switch (ds) {
    case "DELIVERED":        return "delivered";
    case "FAILED":           return "cancelled";
    case "OUT_FOR_DELIVERY":
    case "ACCEPTED":
    case "ASSIGNED":         return "processing";
    case "UNASSIGNED":       return "pending";
    default:                 return "processing";
  }
}
function getStatusCfg(order) {
  return STATUS_CFG[getOrderStatus(order)] || STATUS_CFG.processing;
}

/* ─── helpers ─── */
const fmt = (d, lang="en") =>
  new Date(d).toLocaleDateString(lang==="ar"?"ar-MA":lang==="fr"?"fr-FR":"en-US",{ month:"short", day:"numeric", year:"numeric" });
const initials = u => (u?.name||u?.email||"U").split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2);

/* ─── useApi ─── */
function useApi(path) {
  const [data,setData]=useState(null); const [loading,setLoading]=useState(true); const [error,setError]=useState(null);
  const load=useCallback(async()=>{ if(!path){setLoading(false);return;} setLoading(true);setError(null);
    try{setData(await apiFetch(path));}catch(e){setError(e.message);}finally{setLoading(false);} },[path]);
  useEffect(()=>{load();},[load]);
  return{data,loading,error,refetch:load};
}

/* ══════════════════════════════════════════════
   MICRO-COMPONENTS
══════════════════════════════════════════════ */
function Stars({ rating=0, size=13 }) {
  return <span style={{ display:"inline-flex", gap:2 }}>{[1,2,3,4,5].map(i=>(
    <Star key={i} size={size} fill={i<=rating?C.gold:"none"} stroke={i<=rating?C.gold:"#c8c0a0"} />
  ))}</span>;
}

function Spinner({ label }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"60px 0", flexDirection:"column", gap:12 }}>
      <div style={{ width:36, height:36, borderRadius:"50%", border:`2px solid ${C.goldLight}`, borderTopColor:C.gold, animation:"cd-spin 0.85s linear infinite" }}/>
      <span style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.15em", fontFamily:"'Montserrat',sans-serif" }}>{label || "LOADING"}</span>
    </div>
  );
}

function ErrMsg({ msg }) {
  return <div style={{ padding:"14px 18px", borderRadius:12, background:C.dangerBg, border:`1px solid rgba(155,48,48,.2)`, color:C.danger, fontSize:13, fontFamily:"'Montserrat',sans-serif" }}>⚠ {msg}</div>;
}

function Card({ children, style={}, hover=true, onClick }) {
  const [h,setH]=useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={()=>hover&&setH(true)} onMouseLeave={()=>hover&&setH(false)}
      style={{ background:C.card, borderRadius:18, border:`1px solid ${h?C.gold:C.border}`,
        boxShadow: h?"0 12px 40px rgba(31,61,58,.13)":"0 2px 16px rgba(31,61,58,.06)",
        transform: h?"translateY(-3px)":"translateY(0)", transition:"all .28s cubic-bezier(.23,1,.32,1)",
        cursor:onClick?"pointer":"default", ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ title, sub }) {
  return (
    <div style={{ marginBottom:22 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
        <div style={{ width:3, height:20, borderRadius:2, background:`linear-gradient(180deg,${C.gold},${C.goldDark})` }}/>
        <h2 style={{ margin:0, fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:500, color:C.text, letterSpacing:"0.02em" }}>{title}</h2>
      </div>
      {sub && <p style={{ margin:"0 0 0 13px", fontSize:11, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.06em" }}>{sub}</p>}
    </div>
  );
}

function StatusBadge({ order }) {
  const cfg = getStatusCfg(order);
  const Icon = cfg.icon;
  const raw = getOrderStatus(order); // !FIX — was reading non-existent `order.status`
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 12px", borderRadius:999,
      background:cfg.bg, color:cfg.color, fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:600, letterSpacing:"0.07em", whiteSpace:"nowrap" }}>
      <Icon size={11}/>{raw.charAt(0).toUpperCase()+raw.slice(1)}
    </span>
  );
}

/* ══════════════════════════════════════════════
   CHART COMPONENTS  (pure SVG, no lib needed)
══════════════════════════════════════════════ */

/** Smooth area/line chart for spending over months */
function SpendingLineChart({ orders }) {
  const W=480, H=150, PAD={ t:16, r:12, b:32, l:44 };
  const months=6;

  // build monthly buckets from real orders
  const now=new Date();
  const buckets=Array.from({length:months},(_,i)=>{
    const d=new Date(now.getFullYear(),now.getMonth()-months+1+i,1);
    return { label:d.toLocaleString("en-US",{month:"short"}), year:d.getFullYear(), month:d.getMonth(), total:0 };
  });
  orders.forEach(o=>{
    const d=new Date(o.createdAt);
    const b=buckets.find(bk=>bk.month===d.getMonth()&&bk.year===d.getFullYear());
    if(b) b.total+=o.totalAmount??0;
  });

  const vals=buckets.map(b=>b.total);
  const maxV=Math.max(...vals,1);
  const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
  const px=(i)=>PAD.l+i*(iW/(months-1));
  const py=(v)=>PAD.t+iH-(v/maxV)*iH;

  // smooth curve via cubic bezier
  const pts=vals.map((v,i)=>({x:px(i),y:py(v)}));
  let path="M"+pts[0].x+","+pts[0].y;
  for(let i=1;i<pts.length;i++){
    const cp1x=pts[i-1].x+(pts[i].x-pts[i-1].x)*0.5, cp1y=pts[i-1].y;
    const cp2x=pts[i].x-(pts[i].x-pts[i-1].x)*0.5,   cp2y=pts[i].y;
    path+=` C${cp1x},${cp1y} ${cp2x},${cp2y} ${pts[i].x},${pts[i].y}`;
  }
  const areaPath=path+` L${pts[pts.length-1].x},${PAD.t+iH} L${pts[0].x},${PAD.t+iH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
      <defs>
        <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.gold} stopOpacity=".25"/>
          <stop offset="100%" stopColor={C.gold} stopOpacity=".02"/>
        </linearGradient>
      </defs>
      {/* grid lines */}
      {[0,.25,.5,.75,1].map(f=>{
        const y=PAD.t+iH*(1-f);
        return <line key={f} x1={PAD.l} y1={y} x2={W-PAD.r} y2={y} stroke={C.border} strokeWidth=".8" strokeDasharray="4 3"/>;
      })}
      {/* y labels */}
      {[0,.5,1].map(f=>(
        <text key={f} x={PAD.l-6} y={PAD.t+iH*(1-f)+4} textAnchor="end"
          style={{ fontSize:9, fill:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>
          MAD {Math.round(maxV*f)}
        </text>
      ))}
      {/* area */}
      <path d={areaPath} fill="url(#spendGrad)"/>
      {/* line */}
      <path d={path} fill="none" stroke={C.gold} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
      {/* dots + x labels */}
      {pts.map((p,i)=>(
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill={C.card} stroke={C.gold} strokeWidth="2"/>
          {vals[i]>0&&<circle cx={p.x} cy={p.y} r="2.5" fill={C.gold}/>}
          <text x={p.x} y={H-6} textAnchor="middle"
            style={{ fontSize:9, fill:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>
            {buckets[i].label}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** Donut chart for order status breakdown */
function StatusDonutChart({ orders }) {
  const { t } = useTranslation();
  const delivered  = orders.filter(o=>getOrderStatus(o)==="delivered").length;
  const processing = orders.filter(o=>getOrderStatus(o)==="processing"||getOrderStatus(o)==="pending").length;
  const cancelled  = orders.filter(o=>getOrderStatus(o)==="cancelled").length;
  const total      = orders.length || 1;

  const segments=[
    { val:delivered,  color:C.success, label:t("dash.status_delivered")  },
    { val:processing, color:C.warn,    label:t("dash.status_processing") },
    { val:cancelled,  color:C.danger,  label:t("dash.status_cancelled")  },
  ];

  const R=52, r=32, cx=70, cy=70;
  let ang=-Math.PI/2;
  const arcs=segments.map(s=>{
    const frac=s.val/total;
    const start=ang; ang+=frac*2*Math.PI;
    const end=ang;
    const x1=cx+R*Math.cos(start),y1=cy+R*Math.sin(start);
    const x2=cx+R*Math.cos(end),  y2=cy+R*Math.sin(end);
    const ix1=cx+r*Math.cos(end),  iy1=cy+r*Math.sin(end);
    const ix2=cx+r*Math.cos(start),iy2=cy+r*Math.sin(start);
    const large=frac>.5?1:0;
    return { ...s, frac, d:`M${x1},${y1} A${R},${R} 0 ${large},1 ${x2},${y2} L${ix1},${iy1} A${r},${r} 0 ${large},0 ${ix2},${iy2} Z` };
  });

  return (
    <div style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
      <svg viewBox="0 0 140 140" style={{ width:120, height:120, flexShrink:0 }}>
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={C.border} strokeWidth="1"/>
        {arcs.map((a,i)=>(
          a.frac>0 && <path key={i} d={a.d} fill={a.color} opacity=".85"/>
        ))}
        <text x={cx} y={cy-6} textAnchor="middle" style={{ fontSize:18, fontFamily:"'Cormorant Garamond',serif", fill:C.text, fontWeight:600 }}>{total}</text>
        <text x={cx} y={cy+10} textAnchor="middle" style={{ fontSize:8, fontFamily:"'Montserrat',sans-serif", fill:C.textMuted, letterSpacing:"0.1em" }}>{t("dash.orders_label")}</text>
      </svg>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {segments.map(s=>(
          <div key={s.label} style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:10, height:10, borderRadius:3, background:s.color, flexShrink:0 }}/>
            <span style={{ fontSize:11, fontFamily:"'Montserrat',sans-serif", color:C.textMid }}>{s.label}</span>
            <span style={{ fontSize:13, fontFamily:"'Cormorant Garamond',serif", color:C.text, fontWeight:600, marginLeft:"auto", paddingLeft:10 }}>{s.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Bar chart — orders per month */
function OrdersBarChart({ orders }) {
  const W=480, H=130, PAD={ t:10,r:12,b:32,l:36 };
  const months=6;
  const now=new Date();
  const buckets=Array.from({length:months},(_,i)=>{
    const d=new Date(now.getFullYear(),now.getMonth()-months+1+i,1);
    return { label:d.toLocaleString("en-US",{month:"short"}), year:d.getFullYear(), month:d.getMonth(), count:0 };
  });
  orders.forEach(o=>{
    const d=new Date(o.createdAt);
    const b=buckets.find(bk=>bk.month===d.getMonth()&&bk.year===d.getFullYear());
    if(b) b.count++;
  });
  const counts=buckets.map(b=>b.count);
  const maxC=Math.max(...counts,1);
  const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
  const bW=iW/months, bPad=bW*0.22;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
      <defs>
        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.forest} stopOpacity=".85"/>
          <stop offset="100%" stopColor={C.forest2} stopOpacity=".5"/>
        </linearGradient>
      </defs>
      {[0,.5,1].map(f=>{
        const y=PAD.t+iH*(1-f);
        return <g key={f}>
          <line x1={PAD.l} y1={y} x2={W-PAD.r} y2={y} stroke={C.border} strokeWidth=".8" strokeDasharray="4 3"/>
          <text x={PAD.l-4} y={y+4} textAnchor="end" style={{ fontSize:9, fill:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>{Math.round(maxC*f)}</text>
        </g>;
      })}
      {buckets.map((b,i)=>{
        const h=(b.count/maxC)*iH||2;
        const x=PAD.l+i*bW+bPad;
        const y=PAD.t+iH-h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bW-bPad*2} height={h} rx="5" fill="url(#barGrad)"/>
            {b.count>0&&<text x={x+(bW-bPad*2)/2} y={y-4} textAnchor="middle" style={{ fontSize:9, fill:C.forest, fontFamily:"'Montserrat',sans-serif", fontWeight:600 }}>{b.count}</text>}
            <text x={PAD.l+i*bW+bW/2} y={H-6} textAnchor="middle" style={{ fontSize:9, fill:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>{b.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Radial progress ring — tier progression (spent vs next-tier threshold) */
function RadialTierRing({ totalSpent }) {
  const { t } = useTranslation();
  // Tier thresholds (USD). `name` is the i18n key suffix; `display` is the localized label.
  const TIERS = [
    { key: "tier_silver",   display: t("dash.tier_silver"),   min: 0,     max: 500  },
    { key: "tier_gold",     display: t("dash.tier_gold"),     min: 500,   max: 1500 },
    { key: "tier_platinum", display: t("dash.tier_platinum"), min: 1500,  max: 3500 },
    { key: "tier_obsidian", display: t("dash.tier_obsidian"), min: 3500,  max: 7500 },
  ];
  let current = TIERS[0];
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (totalSpent >= TIERS[i].min) { current = TIERS[i]; break; }
  }
  const idx = TIERS.indexOf(current);
  const next = TIERS[Math.min(idx+1, TIERS.length-1)];
  const progress = Math.min((totalSpent - current.min) / (current.max - current.min), 1);
  const remaining = Math.max(next.min - totalSpent, 0);

  const cx=80, cy=80, R=62, stroke=8;
  const circ = 2*Math.PI*R;
  const dash = circ * progress;

  return (
    <div style={{ display:"flex", alignItems:"center", gap:22, flexWrap:"wrap" }}>
      <svg viewBox="0 0 160 160" style={{ width:150, height:150, flexShrink:0 }}>
        <defs>
          <linearGradient id="tierGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={C.goldDark}/>
            <stop offset="50%" stopColor={C.gold}/>
            <stop offset="100%" stopColor={C.goldFoil}/>
          </linearGradient>
        </defs>
        {/* track */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={`${C.gold}1a`} strokeWidth={stroke}/>
        {/* progress arc */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="url(#tierGrad)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition:"stroke-dasharray .9s cubic-bezier(.23,1,.32,1)" }}/>
        {/* tier label */}
        <text x={cx} y={cy-10} textAnchor="middle"
          style={{ fontSize:9, fill:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.2em", fontWeight:700 }}>
          {t("dash.current_tier")}
        </text>
        <text x={cx} y={cy+10} textAnchor="middle"
          style={{ fontSize:22, fill:C.forest, fontFamily:"'Cormorant Garamond',serif", fontWeight:500, letterSpacing:"0.04em" }}>
          {current.display}
        </text>
        <text x={cx} y={cy+28} textAnchor="middle"
          style={{ fontSize:10, fill:C.gold, fontFamily:"'Montserrat',sans-serif", fontWeight:600 }}>
          {Math.round(progress*100)}%
        </text>
      </svg>
      <div style={{ display:"flex", flexDirection:"column", gap:12, flex:1, minWidth:140 }}>
        <div>
          <div style={{ fontSize:9, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.15em", fontWeight:700, marginBottom:4 }}>
            {t("dash.next_milestone")}
          </div>
          <div style={{ fontSize:18, fontFamily:"'Cormorant Garamond',serif", color:C.text, lineHeight:1.2 }}>
            {next.display}
          </div>
        </div>
        <div style={{ paddingTop:11, borderTop:`1px solid ${C.border}` }}>
          <div style={{ fontSize:9, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.15em", fontWeight:700, marginBottom:4 }}>
            {t("dash.to_unlock")}
          </div>
          <div style={{ fontSize:18, fontFamily:"'Cormorant Garamond',serif", color:C.gold, fontWeight:600 }}>
            MAD {remaining.toLocaleString()}
          </div>
        </div>
        <div style={{ display:"flex", gap:4, marginTop:2 }}>
          {TIERS.map((tier,i)=>(
            <div key={tier.key} style={{ flex:1, height:3, borderRadius:2,
              background: i<=idx ? `linear-gradient(90deg,${C.goldDark},${C.gold})` : `${C.border}` }}/>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Horizontal stacked bar — spending distribution by category */
function CategorySpendBar({ orders }) {
  const { t } = useTranslation();
  // Aggregate spend by category from order items
  const byCat = {};
  orders.forEach(o => {
    if (!o.isConfirmed) return;
    const items = o.orderData?.items ?? [];
    items.forEach(it => {
      const cat = it.category || it.categoryName || "Other";
      const amt = (parseFloat(it.price)||0) * (it.qty??it.quantity??1);
      byCat[cat] = (byCat[cat]||0) + amt;
    });
  });

  const entries = Object.entries(byCat).sort((a,b)=>b[1]-a[1]).slice(0, 5);
  const total = entries.reduce((s,[,v])=>s+v, 0) || 1;

  // Empty fallback
  if (entries.length === 0) {
    return (
      <div style={{ padding:"20px 4px", fontSize:12, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", fontStyle:"italic" }}>
        {t("dash.no_category_data")}
      </div>
    );
  }

  const palette = [C.forest, C.gold, C.forest3, C.goldDark, C.textMid];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      {/* Stacked bar */}
      <div style={{ display:"flex", height:14, borderRadius:7, overflow:"hidden",
        boxShadow:"inset 0 1px 3px rgba(31,61,58,.08)", background:`${C.border}` }}>
        {entries.map(([cat,val],i)=>{
          const pct = (val/total)*100;
          return (
            <div key={cat} title={`${cat}: MAD ${val.toFixed(0)}`}
              style={{ width:`${pct}%`, background:palette[i%palette.length],
                transition:"width .6s cubic-bezier(.23,1,.32,1)",
                borderRight: i<entries.length-1 ? `1px solid ${C.bg}` : "none" }}/>
          );
        })}
      </div>

      {/* Legend rows */}
      <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
        {entries.map(([cat,val],i)=>{
          const pct = (val/total)*100;
          return (
            <div key={cat} style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:10, height:10, borderRadius:3, background:palette[i%palette.length], flexShrink:0 }}/>
              <span style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", color:C.textMid, flex:1,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cat}</span>
              <span style={{ fontSize:10, fontFamily:"'Montserrat',sans-serif", color:C.textMuted, fontWeight:600, letterSpacing:"0.05em" }}>
                {pct.toFixed(0)}%
              </span>
              <span style={{ fontSize:14, fontFamily:"'Cormorant Garamond',serif", color:C.text, fontWeight:600, minWidth:60, textAlign:"right" }}>
                MAD {val.toFixed(0)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   OVERVIEW PANEL
══════════════════════════════════════════════ */
function OverviewPanel({ user, lang }) {
  const { t } = useTranslation();
  const { data:ordersData, loading, error } = useApi(user?`/api/orders?userId=${user.id}&limit=100`:null);
  const orders = ordersData?.data??[];
  const totalSpent = orders.filter(o=>o.isConfirmed).reduce((s,o)=>s+(o.totalAmount??0),0);
  const delivered  = orders.filter(o=>getOrderStatus(o)==="delivered").length;
  const pending    = orders.filter(o=>getOrderStatus(o)==="processing"||getOrderStatus(o)==="pending").length;
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(lang==="ar"?"ar-MA":lang==="fr"?"fr-FR":"en-US",{month:"long",year:"numeric"}) : "—";

  const stats=[
    { label:t("dash.total_spent"),   value:`MAD ${totalSpent.toLocaleString()}`, icon:TrendingUp,  accent:C.gold    },
    { label:t("dash.orders_placed"), value:orders.length,                     icon:ShoppingBag, accent:C.forest  },
    { label:t("dash.delivered"),     value:delivered,                         icon:CheckCircle, accent:C.success },
    { label:t("dash.in_progress"),   value:pending,                           icon:Clock,       accent:C.warn    },
  ];

  return (
    // !FIX — global left-align: textAlign left on the root + alignItems:flex-start
    // on every flex row, and replace space-between rows so children hug the left.
    <div style={{ textAlign:"left" }}>
      {/* ── Welcome banner (with image background) ── */}
      <div style={{ position:"relative", overflow:"hidden", borderRadius:24,
        border:`1px solid rgba(201,168,76,.3)`, marginBottom:28,
        boxShadow:`0 20px 60px -12px rgba(21,49,46,.35), 0 2px 8px rgba(21,49,46,.08)` }}>

        {/* Background image layer */}
        <div style={{ position:"absolute", inset:0,
          backgroundImage:`url(${BANNER_IMAGE})`,
          backgroundSize:"cover", backgroundPosition:"center",
          filter:"saturate(.85) contrast(1.05)", zIndex:0 }}/>

        {/* Forest-tinted overlay for legibility */}
        <div style={{ position:"absolute", inset:0, zIndex:1,
          background:`linear-gradient(105deg, ${C.forest}f0 0%, ${C.forest}d8 40%, ${C.forest2}a8 75%, ${C.forest3}80 100%)` }}/>

        {/* Gold foil grain overlay */}
        <div style={{ position:"absolute", inset:0, zIndex:2, opacity:.5, mixBlendMode:"overlay",
          background:`radial-gradient(ellipse at 85% 20%, ${C.gold}44 0%, transparent 55%)` }}/>

        {/* Decorative concentric rings */}
        <svg style={{ position:"absolute", right:-40, top:-40, opacity:.18, pointerEvents:"none", zIndex:3 }}
          width="280" height="280" viewBox="0 0 200 200">
          {[92,72,54,38,24,12].map((r,i)=>(
            <circle key={i} cx="100" cy="100" r={r} fill="none"
              stroke={C.goldLight} strokeWidth={i===0?"1.2":".5"} strokeDasharray={i%2?"2 4":"none"}/>
          ))}
          {[0,30,60,90,120,150].map((a,i)=>(
            <line key={i}
              x1={100+94*Math.cos(a*Math.PI/180)} y1={100+94*Math.sin(a*Math.PI/180)}
              x2={100-94*Math.cos(a*Math.PI/180)} y2={100-94*Math.sin(a*Math.PI/180)}
              stroke={C.goldLight} strokeWidth=".3"/>
          ))}
        </svg>

        {/* Corner foil accents */}
        <div style={{ position:"absolute", top:18, left:18, zIndex:4,
          width:28, height:28, borderTop:`1px solid ${C.gold}`, borderLeft:`1px solid ${C.gold}`, opacity:.55 }}/>
        <div style={{ position:"absolute", bottom:18, right:18, zIndex:4,
          width:28, height:28, borderBottom:`1px solid ${C.gold}`, borderRight:`1px solid ${C.gold}`, opacity:.55 }}/>

        {/* Content */}
        <div className="cd-welcome-content" style={{ position:"relative", zIndex:5, padding:"44px 44px 40px", textAlign:"left" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", gap:10, marginBottom:14 }}>
            <div style={{ width:22, height:1, background:C.gold }}/>
            <Sparkles size={12} color={C.gold}/>
            <span style={{ fontSize:10, color:C.gold, letterSpacing:"0.28em", fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>
              {t("dash.welcome_back")}
            </span>
          </div>
          <h2 style={{ margin:"0 0 18px", fontSize:"clamp(28px,4vw,44px)", color:"#f5f0e8",
            fontFamily:"'Cormorant Garamond',serif", fontWeight:300, letterSpacing:"0.01em", lineHeight:1.1, textAlign:"left" }}>
            {user?.name||user?.email||t("dash.guest")}
          </h2>
          <div style={{ display:"flex", gap:24, flexWrap:"wrap", alignItems:"center", justifyContent:"flex-start" }}>
            <span style={{ fontSize:12, color:C.gold, display:"flex", alignItems:"center", gap:7,
              fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.1em", fontWeight:600,
              padding:"6px 14px", borderRadius:999, background:"rgba(201,168,76,.12)",
              border:`1px solid ${C.gold}40` }}>
              <Shield size={12}/>{user?.role==="ADMIN"?t("dash.admin"):t("dash.gold_member")}
            </span>
            <span style={{ fontSize:12, color:"rgba(245,240,232,.6)", fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.05em" }}>
              {t("dash.member_since")} · {memberSince}
            </span>
          </div>
        </div>
      </div>

      {/* ── Stat cards ── */}
      {loading?<Spinner/>:error?<ErrMsg msg={error}/>:(
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:14, marginBottom:28 }}>
            {stats.map(({ label, value, icon:Icon, accent })=>(
              <Card key={label} style={{ padding:"24px 22px 22px", position:"relative", overflow:"hidden", textAlign:"left" }}>
                {/* top accent line */}
                <div style={{ position:"absolute", top:0, left:0, right:0, height:2,
                  background:`linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity:.35 }}/>
                {/* !FIX — was justify-content:space-between (icon left, arrow pushed right).
                   Now icon + arrow both sit on the left in a single left-aligned row. */}
                <div style={{ display:"flex", justifyContent:"flex-start", alignItems:"center", gap:10, marginBottom:18 }}>
                  <div style={{ width:40, height:40, borderRadius:12,
                    background:`linear-gradient(135deg, ${accent}1f, ${accent}0a)`,
                    border:`1px solid ${accent}33`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:`0 2px 10px ${accent}14` }}>
                    <Icon size={18} color={accent}/>
                  </div>
                  <ArrowUpRight size={13} color={`${C.textMuted}88`}/>
                </div>
                <div style={{ fontSize:34, fontFamily:"'Cormorant Garamond',serif", color:C.text,
                  lineHeight:1, marginBottom:8, fontWeight:500, letterSpacing:"-0.01em", textAlign:"left" }}>{value}</div>
                <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'Montserrat',sans-serif",
                  letterSpacing:"0.12em", fontWeight:600, textTransform:"uppercase", textAlign:"left" }}>{label}</div>
              </Card>
            ))}
          </div>

          {/* ── Charts row ── */}
          {orders.length>0&&(
            <div className="cd-charts-row" style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:16, marginBottom:24, alignItems:"stretch" }}>
              {/* Spending line chart */}
              <Card style={{ padding:"20px 22px", textAlign:"left" }} hover={false}>
                <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", fontWeight:700, color:C.textMid, letterSpacing:"0.1em", marginBottom:14, textAlign:"left" }}>
                  {t("dash.spending_trend")}
                </div>
                <SpendingLineChart orders={orders}/>
              </Card>

              {/* Status donut */}
              <Card style={{ padding:"20px 22px", minWidth:220, textAlign:"left" }} hover={false}>
                <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", fontWeight:700, color:C.textMid, letterSpacing:"0.1em", marginBottom:14, textAlign:"left" }}>
                  {t("dash.order_status")}
                </div>
                <StatusDonutChart orders={orders}/>
              </Card>
            </div>
          )}

          {/* ── Orders bar chart ── */}
          {orders.length>0&&(
            <Card style={{ padding:"22px 24px", marginBottom:16, textAlign:"left" }} hover={false}>
              <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", fontWeight:700, color:C.textMid, letterSpacing:"0.1em", marginBottom:14, textAlign:"left" }}>
                {t("dash.orders_per_month")}
              </div>
              <OrdersBarChart orders={orders}/>
            </Card>
          )}

          {/* ── Tier progression + Category spend ── */}
          {orders.length>0&&(
            <div className="cd-two-col" style={{ display:"grid", gridTemplateColumns:"minmax(280px,1fr) minmax(280px,1.2fr)", gap:16, marginBottom:24 }}>
              {/* Radial tier ring */}
              <Card style={{ padding:"22px 24px", textAlign:"left",
                background:`linear-gradient(135deg, rgba(255,253,248,.96) 0%, rgba(248,242,228,.92) 100%)` }} hover={false}>
                {/* !FIX — header was space-between (title left, sparkle right). Now both left. */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", gap:10, marginBottom:18 }}>
                  <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", fontWeight:700, color:C.textMid, letterSpacing:"0.1em", textTransform:"uppercase" }}>
                    {t("dash.loyalty_progression")}
                  </div>
                  <Sparkles size={13} color={C.gold}/>
                </div>
                <RadialTierRing totalSpent={totalSpent}/>
              </Card>

              {/* Category spend stacked bar */}
              <Card style={{ padding:"22px 24px", textAlign:"left" }} hover={false}>
                {/* !FIX — header was space-between. Both items now hug the left. */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", gap:12, marginBottom:18, flexWrap:"wrap" }}>
                  <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", fontWeight:700, color:C.textMid, letterSpacing:"0.1em", textTransform:"uppercase" }}>
                    {t("dash.spend_by_category")}
                  </div>
                  <span style={{ fontSize:10, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.12em" }}>
                    {t("dash.top_5")}
                  </span>
                </div>
                <CategorySpendBar orders={orders}/>
              </Card>
            </div>
          )}

          {/* ── Recent orders ── */}
          {orders.length>0&&(
            <>
              <SectionTitle title={t("dash.recent_orders")} sub={t("dash.last_3")}/>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {orders.slice(0,3).map(order=>{
                  const items=order.orderData?.items??[];
                  return (
                    <Card key={order.id} style={{ padding:"15px 20px", textAlign:"left" }}>
                      {/* !FIX — was space-between (info left, price/status pushed to far right).
                         Now a single left-aligned row: icon → order info → price → status. */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", flexWrap:"wrap", gap:16 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:13 }}>
                          <div style={{ width:40, height:40, borderRadius:12, background:`${C.gold}14`, border:`1px solid ${C.gold}30`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Package size={17} color={C.gold}/>
                          </div>
                          <div style={{ textAlign:"left" }}>
                            <div style={{ fontSize:14, fontFamily:"'Cormorant Garamond',serif", color:C.text, marginBottom:2 }}>ORD-{String(order.id).padStart(4,"0")}</div>
                            <div style={{ fontSize:11, color:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>
                              {fmt(order.createdAt,lang)} · {items.length} {items.length===1?t("dash.item"):t("dash.items")}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                          <span style={{ fontSize:17, fontFamily:"'Cormorant Garamond',serif", color:C.text }}>MAD {parseFloat(order.totalAmount||0).toFixed(2)}</span>
                          <StatusBadge order={order}/>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   ORDERS PANEL
══════════════════════════════════════════════ */
function OrdersPanel({ user, lang }) {
  const { t } = useTranslation();
  const [expanded,setExpanded]=useState(null);
  const { data:ordersData, loading, error } = useApi(user?`/api/orders?userId=${user.id}&limit=50`:null);
  const orders=ordersData?.data??[];
  if(loading) return <Spinner/>;
  if(error)   return <ErrMsg msg={error}/>;

  return (
    <div>
      <SectionTitle title={t("dash.order_history")} sub={`${orders.length} ${t("dash.total_orders")}`}/>
      {orders.length===0&&(
        <Card style={{ padding:"60px 32px", textAlign:"center" }} hover={false}>
          <ShoppingBag size={38} color={`${C.textMuted}55`} style={{ marginBottom:14 }}/>
          <p style={{ margin:0, fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:C.textMuted }}>{t("dash.no_orders")}</p>
          <p style={{ margin:"8px 0 0", fontFamily:"'Montserrat',sans-serif", fontSize:11, color:C.textMuted }}>{t("dash.start_shopping")}</p>
        </Card>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {orders.map(order=>{
          const isOpen=expanded===order.id;
          const items=order.orderData?.items??[];
          const customer=order.orderData?.customer||{};
          const cfg=getStatusCfg(order);
          const Icon=cfg.icon;
          return (
            <div key={order.id} style={{ background:C.card, borderRadius:18, border:`1px solid ${isOpen?C.gold:C.border}`,
              overflow:"hidden", boxShadow:isOpen?"0 6px 28px rgba(31,61,58,.12)":"0 2px 12px rgba(31,61,58,.05)", transition:"all .28s" }}>
              <button onClick={()=>setExpanded(isOpen?null:order.id)} className="cd-order-row"
                style={{ width:"100%", background:"none", border:"none", cursor:"pointer", padding:"17px 20px",
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:42, height:42, borderRadius:13, background:cfg.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <Icon size={19} color={cfg.color}/>
                  </div>
                  <div style={{ textAlign:"left" }}>
                    <div style={{ fontSize:15, fontFamily:"'Cormorant Garamond',serif", color:C.text }}>ORD-{String(order.id).padStart(4,"0")}</div>
                    <div style={{ fontSize:11, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", marginTop:2 }}>{fmt(order.createdAt,lang)}</div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                  <StatusBadge order={order}/>
                  <span style={{ fontSize:17, fontFamily:"'Cormorant Garamond',serif", color:C.text, minWidth:64, textAlign:"right" }}>MAD {parseFloat(order.totalAmount||0).toFixed(2)}</span>
                  <ChevronDown size={15} color={C.textMuted} style={{ transform:isOpen?"rotate(180deg)":"none", transition:"transform .3s", flexShrink:0 }}/>
                </div>
              </button>

              {isOpen&&(
                <div style={{ borderTop:`1px solid ${C.border}`, padding:"18px 20px", background:"rgba(245,240,232,.5)" }}>
                  {(customer.address||customer.city)&&(
                    <div style={{ display:"flex", alignItems:"flex-start", gap:7, marginBottom:16, padding:"10px 14px",
                      borderRadius:10, background:"rgba(255,253,248,.7)", border:`1px solid ${C.border}` }}>
                      <MapPin size={13} color={C.gold} style={{ marginTop:1, flexShrink:0 }}/>
                      <span style={{ fontSize:12, color:C.textMid, fontFamily:"'Montserrat',sans-serif", lineHeight:1.6 }}>
                        {[customer.address,customer.city,customer.zip].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                  {items.length===0&&<p style={{ fontSize:12, color:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>{t("dash.no_item_details")}</p>}
                  {items.map((item,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"11px 0", borderBottom:i<items.length-1?`1px solid ${C.border}`:"none", gap:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:11 }}>
                        <div style={{ width:36, height:36, borderRadius:10, background:`${C.gold}14`, overflow:"hidden",
                          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {item.img?<img src={item.img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                            :<ShoppingBag size={15} color={C.gold}/>}
                        </div>
                        <div>
                          <div style={{ fontSize:13, color:C.text, fontFamily:"'Cormorant Garamond',serif" }}>{item.name}</div>
                          <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", marginTop:1 }}>
                            {t("dash.qty")}: {item.qty??item.quantity??1}{item.size?` · ${item.size}`:""}
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize:14, fontFamily:"'Cormorant Garamond',serif", color:C.text, fontWeight:600, flexShrink:0 }}>
                        MAD {parseFloat(item.price||0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${C.border}`, display:"flex", justifyContent:"flex-end" }}>
                    <div>
                      <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.1em", marginBottom:3 }}>{t("dash.order_total").toUpperCase()}</div>
                      <div style={{ fontSize:22, fontFamily:"'Cormorant Garamond',serif", color:C.gold, fontWeight:600 }}>MAD {parseFloat(order.totalAmount||0).toFixed(2)}</div>
                    </div>
                  </div>
                  {getOrderStatus(order)==="delivered"&&(
                    <div style={{ marginTop:14, display:"flex", gap:9, flexWrap:"wrap" }}>
                      {[{icon:RotateCcw,label:t("dash.reorder")},{icon:Star,label:t("dash.write_review")}].map(({icon:Ic,label})=>(
                        <button key={label} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:999,
                          border:`1px solid ${C.border}`, background:"transparent", color:C.textMid,
                          fontFamily:"'Montserrat',sans-serif", fontSize:11, letterSpacing:"0.06em", cursor:"pointer", transition:"all .2s" }}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor=C.gold;e.currentTarget.style.color=C.forest;}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.textMid;}}>
                          <Ic size={11}/>{label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   PROFILE PANEL
══════════════════════════════════════════════ */
function ProfilePanel({ user, onSignOut }) {
  const { t } = useTranslation();
  const ini=initials(user);
  const mSince=user?.createdAt?new Date(user.createdAt).toLocaleDateString("en-US",{month:"long",year:"numeric"}):"—";
  const fields=[
    {label:t("dash.full_name"),    value:user?.name||"—",     icon:User   },
    {label:t("dash.email"),        value:user?.email||"—",    icon:Bell   },
    {label:t("dash.member_since"), value:mSince,               icon:Shield },
    {label:t("dash.role"),         value:user?.role==="ADMIN"?t("dash.admin"):t("dash.gold_member"), icon:Gift },
  ];
  return (
    <div>
      <SectionTitle title={t("dash.my_profile")}/>
      <Card style={{ padding:"26px 28px", marginBottom:18 }} hover={false}>
        <div style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flexShrink:0 }}>
            <div style={{ width:82, height:82, borderRadius:"50%",
              background:`linear-gradient(135deg,${C.forest},${C.forest2})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              border:`2px solid ${C.gold}88`, boxShadow:`0 0 0 5px ${C.gold}14` }}>
              <span style={{ fontSize:28, fontFamily:"'Cormorant Garamond',serif", color:C.gold }}>{ini}</span>
            </div>
            <button style={{ position:"absolute", bottom:2, right:2, width:26, height:26, borderRadius:"50%",
              background:C.gold, border:`2px solid ${C.bg}`, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Camera size={11} color={C.forest}/>
            </button>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <h3 style={{ margin:"0 0 3px", fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.text, fontWeight:500 }}>
              {user?.name||user?.email||t("dash.guest")}
            </h3>
            <p style={{ margin:"0 0 10px", fontFamily:"'Montserrat',sans-serif", fontSize:12, color:C.textMuted }}>{user?.email}</p>
            <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 13px", borderRadius:999,
              background:`${C.gold}18`, border:`1px solid ${C.gold}44`, color:C.warn,
              fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, letterSpacing:"0.08em" }}>
              <Shield size={11}/>{user?.role==="ADMIN"?t("dash.admin"):t("dash.gold_member")}
            </span>
          </div>
        </div>
      </Card>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12, marginBottom:22 }}>
        {fields.map(({label,value,icon:Icon})=>(
          <Card key={label} style={{ padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:7 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Icon size={12} color={C.textMuted}/>
                <span style={{ fontSize:10, color:C.textMuted, letterSpacing:"0.12em", fontFamily:"'Montserrat',sans-serif", fontWeight:600 }}>{label.toUpperCase()}</span>
              </div>
              <Edit3 size={12} color={`${C.textMuted}66`} style={{ cursor:"pointer" }}/>
            </div>
            <div style={{ fontSize:16, color:C.text, fontFamily:"'Cormorant Garamond',serif" }}>{value}</div>
          </Card>
        ))}
      </div>
      <button onClick={onSignOut} style={{ display:"flex", alignItems:"center", gap:8, padding:"11px 22px", borderRadius:999,
        border:`1px solid rgba(155,48,48,.3)`, background:"transparent", color:C.danger,
        fontFamily:"'Montserrat',sans-serif", fontSize:12, letterSpacing:"0.08em", cursor:"pointer", transition:"all .2s" }}
        onMouseEnter={e=>{e.currentTarget.style.background=C.dangerBg;}}
        onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
        <LogOut size={13}/>{t("dash.sign_out")}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════
   REVIEWS PANEL
══════════════════════════════════════════════ */
function ReviewsPanel() {
  const { t } = useTranslation();
  const { data:reviews, loading, error, refetch } = useApi("/api/reviews");
  const handleDelete=async(id)=>{
    if(!window.confirm(t("dash.confirm_delete")))return;
    try{await apiFetch(`/api/reviews/${id}`,{method:"DELETE"});refetch();}catch(e){alert(e.message);}
  };
  if(loading)return<Spinner/>;
  if(error)  return<ErrMsg msg={error}/>;
  const list=Array.isArray(reviews)?reviews:[];
  return (
    <div>
      <SectionTitle title={t("dash.my_reviews")} sub={`${list.length} ${t("dash.reviews_written")}`}/>
      {list.length===0&&(
        <Card style={{ padding:"60px 32px", textAlign:"center" }} hover={false}>
          <Star size={36} color={`${C.textMuted}55`} style={{ marginBottom:14 }}/>
          <p style={{ margin:0, fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:C.textMuted }}>{t("dash.no_reviews")}</p>
        </Card>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {list.map(r=>(
          <Card key={r.id} style={{ padding:"20px 22px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10, marginBottom:12 }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:C.text, marginBottom:7 }}>
                  {r.product?.name??`${t("dash.product")} #${r.productId}`}
                </div>
                <Stars rating={r.rating}/>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:11, color:C.textMuted, fontFamily:"'Montserrat',sans-serif" }}>{fmt(r.createdAt)}</span>
                <button onClick={()=>handleDelete(r.id)} style={{ padding:"4px 12px", borderRadius:999,
                  border:`1px solid rgba(155,48,48,.25)`, background:"transparent",
                  color:C.danger, fontSize:11, fontFamily:"'Montserrat',sans-serif",
                  cursor:"pointer", display:"flex", alignItems:"center", gap:5, transition:"background .2s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=C.dangerBg}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <Trash2 size={10}/>{t("dash.delete")}
                </button>
              </div>
            </div>
            {r.comment&&(
              <p style={{ margin:0, fontSize:13, color:C.textMid, lineHeight:1.75, fontStyle:"italic",
                fontFamily:"'Cormorant Garamond',serif", borderLeft:`3px solid ${C.gold}66`, paddingLeft:14 }}>
                "{r.comment}"
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   WISHLIST PANEL
══════════════════════════════════════════════ */
function WishlistPanel() {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApi("/api/favorites?includeProduct=true");
  const handleRemove=async(productId)=>{
    try{await apiFetch(`/api/favorites/${productId}`,{method:"DELETE"});refetch();}catch(e){alert(e.message);}
  };
  const addToCart=(item)=>{
    try{
      const cart=JSON.parse(localStorage.getItem("dar_oulmes_cart")||"[]");
      const ex=cart.find(c=>c.id===item.id);
      if(ex)ex.quantity+=1; else cart.push({id:item.id,name:item.name,price:item.price,img:item.imageUrl,tag:item.tag||"",quantity:1});
      localStorage.setItem("dar_oulmes_cart",JSON.stringify(cart));
      window.dispatchEvent(new Event("cart-updated"));
    }catch{}
  };
  if(loading)return<Spinner/>;
  if(error)  return<ErrMsg msg={error}/>;
  const list=Array.isArray(data)?data:[];
  return (
    <div>
      <SectionTitle title={t("dash.saved_items")} sub={`${list.length} ${t("dash.pieces_saved")}`}/>
      {list.length===0&&(
        <Card style={{ padding:"60px 32px", textAlign:"center" }} hover={false}>
          <Heart size={36} color={`${C.textMuted}55`} style={{ marginBottom:14 }}/>
          <p style={{ margin:0, fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:C.textMuted }}>{t("dash.no_saved")}</p>
        </Card>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14 }}>
        {list.map(fav=>{
          const item=fav.product??{};
          return (
            <Card key={fav.id} style={{ overflow:"hidden" }}>
              <div style={{ height:150, background:`linear-gradient(135deg,${C.goldLight}44,${C.gold}18)`, position:"relative", overflow:"hidden" }}>
                {item.imageUrl
                  ?<img src={item.imageUrl} alt={item.name} style={{ width:"100%",height:"100%",objectFit:"cover",transition:"transform .5s" }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.07)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
                  :<div style={{ width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <ShoppingBag size={38} color={`${C.gold}55`}/>
                    </div>}
                <button onClick={()=>handleRemove(fav.productId)}
                  style={{ position:"absolute",top:9,right:9,width:28,height:28,borderRadius:"50%",
                    background:"rgba(255,252,248,.9)",border:"none",cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s" }}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(155,48,48,.1)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(255,252,248,.9)"}>
                  <Heart size={13} fill="#e05555" stroke="#e05555"/>
                </button>
                {item.tag&&<span style={{ position:"absolute",top:9,left:9,fontSize:8,fontFamily:"'Montserrat',sans-serif",fontWeight:700,letterSpacing:"0.12em",padding:"3px 8px",borderRadius:4,background:C.gold,color:"#fff" }}>{item.tag}</span>}
              </div>
              <div style={{ padding:"14px 14px 16px" }}>
                {item.category?.name&&<span style={{ fontSize:9,color:C.textMuted,fontFamily:"'Montserrat',sans-serif",letterSpacing:"0.1em",fontWeight:600 }}>{item.category.name.toUpperCase()}</span>}
                <div style={{ fontFamily:"'Cormorant Garamond',serif",fontSize:16,color:C.text,margin:"5px 0 7px",lineHeight:1.3 }}>{item.name??"—"}</div>
                {item.rating!=null&&<div style={{ display:"flex",alignItems:"center",gap:5,marginBottom:10 }}><Stars rating={Math.round(item.rating)} size={11}/><span style={{ fontSize:10,color:C.textMuted,fontFamily:"'Montserrat',sans-serif" }}>{Number(item.rating).toFixed(1)}</span></div>}
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                  <span style={{ fontSize:19,fontFamily:"'Cormorant Garamond',serif",color:C.text }}>MAD {item.price??"—"}</span>
                  <button onClick={()=>addToCart(item)} style={{ padding:"6px 14px",borderRadius:999,
                    background:`linear-gradient(135deg,${C.forest},${C.forest2})`,border:"none",
                    color:C.gold,fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                    letterSpacing:"0.1em",cursor:"pointer",transition:"opacity .2s" }}
                    onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                    {t("dash.add_to_cart")}
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════ */
export default function CustomerDashboard({ onSignOut }) {
  const { t, i18n } = useTranslation();
  const [activeLang,setActiveLang]=useState(i18n.language?.slice(0,2)||"en");
  const [activeTab,setActiveTab]=useState("overview");

  const [user]=useState(()=>{
    try{ return JSON.parse(localStorage.getItem("dar_oulmes_user")||"null")||JSON.parse(localStorage.getItem("user")||"null"); }
    catch{ return null; }
  });

  const switchLang=(code)=>{ setActiveLang(code); i18n.changeLanguage(code); };

  const handleSignOut=()=>{
    ["dar_oulmes_token","dar_oulmes_user","access_token","user"].forEach(k=>localStorage.removeItem(k));
    if(typeof onSignOut==="function")onSignOut();
  };

  const isRtl=activeLang==="ar";

  const TABS=[
    {id:"overview", labelKey:"dash.overview", icon:TrendingUp  },
    {id:"orders",   labelKey:"dash.orders",   icon:ShoppingBag },
    {id:"profile",  labelKey:"dash.profile",  icon:User        },
    {id:"reviews",  labelKey:"dash.reviews",  icon:Star        },
    {id:"wishlist", labelKey:"dash.wishlist", icon:Heart       },
  ];

  const ini=initials(user);

  const renderPanel=()=>{
    const p={user,lang:activeLang};
    switch(activeTab){
      case"overview": return<OverviewPanel {...p}/>;
      case"orders":   return<OrdersPanel   {...p}/>;
      case"profile":  return<ProfilePanel  user={user} onSignOut={handleSignOut}/>;
      case"reviews":  return<ReviewsPanel/>;
      case"wishlist": return<WishlistPanel/>;
      default:        return null;
    }
  };

  return (
    <div dir={isRtl?"rtl":"ltr"} style={{ minHeight:"100vh", fontFamily:"'Cormorant Garamond',serif", overflowX:"hidden", position:"relative" }}>
      <style>{CSS}</style>

      {/* Decorative bg pattern */}
      <svg style={{ position:"fixed",inset:0,width:"100%",height:"100%",pointerEvents:"none",zIndex:0,opacity:.055 }} xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="cdgrid" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
            <polygon points="60,5 115,35 115,85 60,115 5,85 5,35" fill="none" stroke={C.forest} strokeWidth="0.7"/>
            <circle cx="60" cy="60" r="8" fill="none" stroke={C.gold} strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#cdgrid)"/>
      </svg>
      {/* Ambient top glow */}
      <div style={{ position:"fixed",top:-100,left:"20%",width:600,height:400,borderRadius:"50%",background:`radial-gradient(circle,rgba(201,168,76,.08) 0%,transparent 65%)`,pointerEvents:"none",zIndex:0,filter:"blur(40px)" }}/>

      <div className="cd-shell" style={{ position:"relative", zIndex:1, maxWidth:1200, margin:"0 auto", padding:"56px 28px 100px" }}>

        {/* ══ TOP BAR ══ */}


        {/* Gold shimmer rule */}
        <div style={{ height:1, background:`linear-gradient(90deg,transparent,${C.gold} 30%,${C.gold} 70%,transparent)`, margin:"8px 0 34px", opacity:.4 }}/>

        {/* ══ BODY ══ */}
        <div className="cd-body" style={{ display:"flex", gap:28, alignItems:"flex-start" }}>

          {/* Sidebar */}
          <aside className="cd-sidebar">
            <div style={{ background:"rgba(255,253,248,.94)",
              border:`1px solid ${C.border}`, borderRadius:24,
              boxShadow:"0 12px 40px -8px rgba(21,49,46,.12), 0 2px 8px rgba(21,49,46,.04)",
              overflow:"hidden", backdropFilter:"blur(8px)" }}>

              {/* User card */}
              <div style={{ padding:"22px 18px 18px", background:`linear-gradient(180deg,rgba(31,61,58,.06) 0%,transparent 100%)`,
                borderBottom:`1px solid ${C.border}`, textAlign:"center" }}>
                <div style={{ position:"relative", display:"inline-block", marginBottom:12 }}>
                  <div style={{ width:58, height:58, borderRadius:"50%",
                    background:`linear-gradient(135deg,${C.forest},${C.forest2})`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    border:`2px solid ${C.gold}77`, boxShadow:`0 0 0 4px ${C.gold}14` }}>
                    <span style={{ fontSize:19, color:C.gold }}>{ini}</span>
                  </div>
                  <span style={{ position:"absolute",bottom:2,right:2,width:12,height:12,borderRadius:"50%",
                    background:"#4ade80",border:`2px solid ${C.bg}` }}/>
                </div>
                <div style={{ fontSize:14, color:C.text, marginBottom:2 }}>{user?.name?.split(" ")[0]||user?.email||t("dash.guest")}</div>
                <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.1em" }}>
                  {user?.role==="ADMIN"?t("dash.admin").toUpperCase():t("dash.gold_member").toUpperCase()}
                </div>
              </div>

              {/* Nav */}
              <nav style={{ padding:"10px 8px" }}>
                {TABS.map(({id,labelKey,icon:Icon})=>(
                  <button key={id} className={`cd-tab-btn${activeTab===id?" active":""}`} onClick={()=>setActiveTab(id)}>
                    <Icon size={14}/>
                    <span>{t(labelKey)}</span>
                    {activeTab!==id&&<ChevronRight size={12} style={{ marginLeft:"auto", opacity:.35 }}/>}
                  </button>
                ))}
              </nav>

              {/* Member since */}
              <div style={{ margin:"0 8px 10px", padding:"11px 13px", borderRadius:13,
                background:`${C.gold}0d`, border:`1px solid ${C.gold}28` }}>
                <div style={{ fontSize:9, color:C.gold, letterSpacing:"0.15em", fontFamily:"'Montserrat',sans-serif", fontWeight:700, marginBottom:3 }}>
                  {t("dash.member_since").toUpperCase()}
                </div>
                <div style={{ fontSize:13, color:C.textMid, fontFamily:"'Montserrat',sans-serif" }}>
                  {user?.createdAt?new Date(user.createdAt).toLocaleDateString("en-US",{month:"short",year:"numeric"}):"—"}
                </div>
              </div>
            </div>
          </aside>

          {/* Main */}
          <main className="cd-main panel-anim" key={activeTab}>{renderPanel()}</main>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="cd-bottom-nav">
        {TABS.map(({id,labelKey,icon:Icon})=>(
          <button key={id} className={`cd-bnav-btn${activeTab===id?" active":""}`} onClick={()=>setActiveTab(id)}>
            <Icon size={19} color={activeTab===id?C.forest:C.textMuted}/>
            <span>{t(labelKey)}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ══════════════════════════════════════════════
   GLOBAL CSS
══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  @keyframes cd-spin { to { transform:rotate(360deg); } }
  @keyframes cd-fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .panel-anim { animation: cd-fadeUp .35s cubic-bezier(.23,1,.32,1); }

  .cd-topbar {
    display:flex; align-items:center; justify-content:space-between;
    margin-bottom:18px; flex-wrap:wrap; gap:12px;
  }

  /* Lang bar */
  .cd-lang-bar {
    display:inline-flex; gap:3px;
    background:rgba(255,252,248,.85); border:1px solid rgba(201,168,76,.28);
    border-radius:30px; padding:3px;
    box-shadow: 0 2px 8px rgba(31,61,58,.06);
  }
  .cd-lang-btn {
    padding:5px 14px; border-radius:24px; border:none; cursor:pointer;
    font-family:'Montserrat',sans-serif; font-size:10px; font-weight:700;
    letter-spacing:.1em; transition:all .2s;
    background:transparent; color:#9a8a6a;
  }
  .cd-lang-btn.active { background:#c9a84c; color:#fff; box-shadow:0 2px 8px rgba(201,168,76,.4); }
  .cd-lang-btn:hover:not(.active) { color:#1f3d3a; }

  /* Icon button */
  .cd-icon-btn {
    position:relative; width:38px; height:38px; border-radius:50%;
    background:rgba(255,252,248,.85); border:1px solid rgba(201,168,76,.25);
    cursor:pointer; display:flex; align-items:center; justify-content:center;
    transition:all .2s;
  }
  .cd-icon-btn:hover { border-color:#c9a84c; box-shadow:0 2px 12px rgba(201,168,76,.2); }

  /* Sign out */
  .cd-signout-btn {
    display:flex; align-items:center; gap:7px; padding:7px 16px; border-radius:999px;
    background:rgba(255,252,248,.85); border:1px solid rgba(201,168,76,.25);
    color:#9a8a6a; font-family:'Montserrat',sans-serif; font-size:11px;
    font-weight:600; letter-spacing:.08em; cursor:pointer; transition:all .2s;
  }
  .cd-signout-btn:hover { background:rgba(155,48,48,.07); border-color:rgba(155,48,48,.3); color:#9b3030; }

  /* Sidebar */
  .cd-sidebar { width:228px; flex-shrink:0; position:sticky; top:40px; }

  /* Tab button */
  .cd-tab-btn {
    display:flex; align-items:center; gap:9px; width:100%; padding:10px 13px;
    border:none; background:none; border-radius:12px; cursor:pointer; text-align:left;
    font-family:'Montserrat',sans-serif; font-size:12px; font-weight:500;
    letter-spacing:.05em; color:#9a8a6a; transition:all .22s; margin-bottom:2px;
  }
  .cd-tab-btn:hover { background:rgba(201,168,76,.1); color:#1f3d3a; }
  .cd-tab-btn.active {
    background:linear-gradient(135deg,rgba(31,61,58,.92),rgba(45,92,87,.92));
    color:#c9a84c; box-shadow:0 4px 16px rgba(31,61,58,.18);
  }
  .cd-tab-btn.active svg { stroke:#c9a84c; }

  /* Main */
  .cd-main { flex:1; min-width:0; }

  /* Bottom nav */
  .cd-bottom-nav {
    display:none; position:fixed; bottom:0; left:0; right:0; z-index:100;
    background:rgba(245,240,232,.97); border-top:1px solid rgba(201,168,76,.25);
    backdrop-filter:blur(20px); padding:8px 0 14px; justify-content:space-around;
  }
  .cd-bnav-btn {
    display:flex; flex-direction:column; align-items:center; gap:4px;
    background:none; border:none; cursor:pointer; padding:5px 10px;
    font-family:'Montserrat',sans-serif; font-size:9px; font-weight:600;
    letter-spacing:.06em; color:#9a8a6a; transition:color .2s;
  }
  .cd-bnav-btn.active { color:#1f3d3a; }

  @media(max-width:760px) {
    .cd-shell { padding: 24px 14px 96px !important; }
    .cd-body { gap: 0 !important; }
    .cd-sidebar { display:none !important; }
    .cd-main    { min-width:0 !important; width:100% !important; }
    .cd-bottom-nav { display:flex !important; }

    /* Welcome banner — tighten padding so it fits a phone */
    .cd-welcome-content { padding: 26px 20px 24px !important; }

    /* Charts row — was "1fr auto" forcing two columns; stack on mobile */
    .cd-charts-row { grid-template-columns: 1fr !important; }
    .cd-charts-row > * { min-width: 0 !important; }

    /* Tier + category — was two minmax(280px,...) cols that overflow on phones */
    .cd-two-col { grid-template-columns: 1fr !important; }

    /* Orders panel header row — keep info on top, status/price/chevron on a new line */
    .cd-order-row { padding: 14px 16px !important; }
  }

  @media(max-width:420px) {
    .cd-shell { padding: 18px 10px 96px !important; }
    .cd-welcome-content { padding: 22px 16px 20px !important; }
  }
`;